import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../../schemaBase.js";
import type {
    SproutVerificationInfoFieldsWithDefaults,
    SproutVerificationInfoSchemaTypes,
} from "../../../types/sproutVerificationInfo.d.js";
import { type DtoValidatorTypes } from "../../../validatableClass.js";
import { SproutVerificationIndex } from "./sproutVerificationIndex.js";

const SCHEMA_KEY = "SproutVerificationInfo" as const;

/**
 * Root container for sprout irrigation verification data. This model serves as the top-level
 * entry point for all verification information associated with a device.
 *
 * The verification system tracks whether irrigation events (valve open/close operations)
 * occurred as intended by monitoring device sensors, valve states, timing, and connectivity.
 *
 * @see SproutVerificationIndex - Contains verification instances organized by valve state
 * @see SproutVerificationInstance - Individual verification records for specific valve states
 */
// TODO: convert to responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SproutVerificationInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutVerificationInfo extends SchemaBase<
    SproutVerificationInfo,
    SproutVerificationInfoFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * Organized collection of verification instances, indexed by valve state combinations.
     * Each valve state (e.g., 0b00, 0b01, 0b10, 0b11) maps to verification data for when
     * the device was in that particular valve configuration.
     */
    @validate({ childClass: SproutVerificationIndex, responseOnly: true })
    @prop({ type: () => SproutVerificationIndex, required: true })
    public verificationsByValveState!: SproutVerificationIndex;

    /**
     * Optional timestamp indicating when the device next requires verification.
     * Used to schedule future verification checks for ongoing irrigation events.
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public needsVerifyingAt?: Date;

    static getters = {} as const satisfies Record<string, (self: SproutVerificationInfo) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutVerificationInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SproutVerificationInfo, SproutVerificationInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutVerificationInfo.dtoValidators = getZodSchemaFromClass(
    SproutVerificationInfo,
) as unknown as DtoValidatorTypes<SproutVerificationInfoSchemaTypes>;
